/* ============================================================
   Recherche interne — maquette 4D « par étage de la maison »
   - <predictive-search-component> : autocomplétion du popup header
     (Predictive Search API + Section Rendering, debounce, AbortController,
     navigation clavier)
   - <search-page-component> : page de résultats (facettes AJAX via
     Section Rendering API, cartes étage -> filtre hauteur, historique URL)
   ============================================================ */

/* Tranches « étage » -> plage de hauteur en mètres.
   Le mapping vers les valeurs réelles du filtre hauteur (Search & Discovery)
   est calculé dynamiquement en parsant les libellés des valeurs. */
const ETAGE_BUCKETS = {
  rdc: { min: 0, max: 3.5 },
  e1: { min: 3.5, max: 6.5 },
  e2: { min: 6.5, max: 8.5 },
  toit: { min: 8.5, max: Infinity },
};

function parseHauteur(label) {
  const match = String(label).replace(',', '.').match(/(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : null;
}

/* « roulant 4m » -> « roulant 4 m » : Shopify indexe « 4 m » comme deux
   termes, « 4m » collé ne matche pas les titres. On insère l'espace manquant
   entre un chiffre et des lettres, SAUF si les lettres sont elles-mêmes
   suivies d'un chiffre : « 2m80 » existe tel quel dans les titres produits
   et « 2 m 80 » ne donne aucun résultat. */
function normalizeQuery(query) {
  return String(query).replace(/(\d)([a-zA-Z]+)(?!\d)/g, '$1 $2');
}

/* ══════════ Autocomplétion (popup header) ══════════ */

class PredictiveSearchComponent extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[name="q"]');
    this.results = this.querySelector('[data-ref="predictive-results"]');
    this.searchUrl = this.dataset.searchUrl || '/search';
    this.predictiveUrl = this.dataset.predictiveUrl || '/search/suggest';
    this.sectionId = this.dataset.resultsSection || 'predictive-search';
    this.debounceTimer = null;
    this.abortController = null;
    this.selectedIndex = -1;

    if (!this.input || !this.results) return;

    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-expanded', 'false');

    this.input.addEventListener('input', () => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.search(), 300);
    });

    this.input.addEventListener('keydown', (event) => this.onKeydown(event));

    /* Soumission (Entrée / clic loupe) : requête normalisée avant navigation */
    const form = this.querySelector('form[role="search"]');
    if (form) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const params = new URLSearchParams();
        params.set('q', normalizeQuery(this.input.value.trim()));
        params.set('options[prefix]', 'last');
        window.location.href = `${this.searchUrl}?${params.toString()}`;
      });
    }

    /* Les chips « étage » emportent la requête en cours vers la page de résultats */
    this.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-etage-chip]');
      if (!chip) return;
      event.preventDefault();
      const params = new URLSearchParams();
      /* Champ vide -> q=* : recherche « tout le catalogue », filtrée par la hauteur */
      params.set('q', normalizeQuery(this.input.value.trim()) || '*');
      params.set('etage', chip.dataset.etageChip);
      window.location.href = `${this.searchUrl}?${params.toString()}`;
    });
  }

  async search() {
    const query = normalizeQuery(this.input.value.trim());

    if (this.abortController) this.abortController.abort();

    if (query.length < 2) {
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      return;
    }

    this.abortController = new AbortController();
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last',
      section_id: this.sectionId,
    });

    try {
      const response = await fetch(`${this.predictiveUrl}?${params.toString()}`, {
        signal: this.abortController.signal,
      });
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-predictive-content]');
      this.results.innerHTML = content ? content.innerHTML : '';
      this.selectedIndex = -1;
      this.input.setAttribute('aria-expanded', this.results.innerHTML.trim() !== '' ? 'true' : 'false');
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.results.innerHTML = '';
      }
    }
  }

  onKeydown(event) {
    const items = Array.from(this.results.querySelectorAll('.sp-item'));
    if (!items.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      this.selectedIndex = (this.selectedIndex + delta + items.length) % items.length;
      items.forEach((item, index) => {
        item.setAttribute('aria-selected', index === this.selectedIndex ? 'true' : 'false');
      });
      items[this.selectedIndex].scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && this.selectedIndex >= 0) {
      event.preventDefault();
      window.location.href = items[this.selectedIndex].href;
    }
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}

/* ══════════ Page de résultats ══════════ */

class SearchPageComponent extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;

    /* Délégation d'événements : le contenu est remplacé à chaque AJAX,
       les écouteurs posés sur le composant racine survivent. */
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('change', (event) => this.onChange(event));

    /* Recherche en direct : la frappe dans le champ de la page relance la
       recherche en AJAX (même debounce que le popup), sans clic sur la loupe. */
    this.addEventListener('input', (event) => {
      const qInput = event.target.closest('form[role="search"] input[name="q"]');
      if (!qInput) return;
      clearTimeout(this.liveTimer);
      this.liveTimer = setTimeout(() => this.liveSearch(qInput.value), 300);
    });
    this.addEventListener('submit', (event) => {
      const form = event.target.closest('form[role="search"]');
      if (!form) return;
      event.preventDefault();
      clearTimeout(this.liveTimer);
      const qInput = form.querySelector('input[name="q"]');
      this.liveSearch(qInput ? qInput.value : '', true);
    });

    window.addEventListener('popstate', () => this.fetchAndSwap(window.location.href, false));

    this.readHauteurData();
    this.syncEtageCards();

    /* Arrivée depuis une chip du popup : ?etage=xxx -> applique le filtre hauteur */
    const params = new URLSearchParams(window.location.search);
    const etage = params.get('etage');
    if (etage && ETAGE_BUCKETS[etage]) {
      params.delete('etage');
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
      this.applyBucket(etage);
    }
  }

  readHauteurData() {
    this.hauteur = null;
    const dataScript = this.querySelector('[data-ref="hauteur-data"]');
    if (!dataScript) return;
    try {
      this.hauteur = JSON.parse(dataScript.textContent);
    } catch (error) {
      this.hauteur = null;
    }
  }

  /* Valeurs du filtre hauteur appartenant à une tranche étage */
  bucketValues(bucketKey) {
    if (!this.hauteur || this.hauteur.type === 'price_range') return [];
    const bucket = ETAGE_BUCKETS[bucketKey];
    return (this.hauteur.values || []).filter((value) => {
      const h = parseHauteur(value.label);
      return h !== null && h > bucket.min && h <= bucket.max;
    });
  }

  /* État visuel des cartes.
     - Filtre absent : cartes neutres, jamais grisées.
     - Filtre plage numérique (price_range) : pas de liste de valeurs côté
       Shopify -> jamais de grisage ; la carte active est celle dont les
       bornes correspondent aux bornes gte/lte appliquées.
     - Filtre liste : active si toutes les valeurs actives correspondent
       exactement à la tranche ; grisée si la tranche n'a aucune valeur. */
  syncEtageCards() {
    const cards = this.querySelectorAll('[data-etage-carte]');
    if (!cards.length) return;

    if (!this.hauteur) {
      cards.forEach((card) => {
        card.classList.remove('se-carte--vide');
        card.setAttribute('aria-pressed', 'false');
      });
      return;
    }

    if (this.hauteur.type === 'price_range') {
      const toNumber = (raw) => {
        const parsed = parseFloat(raw);
        return Number.isFinite(parsed) ? parsed : null;
      };
      const appliedMin = toNumber(this.hauteur.applied_min);
      const appliedMax = toNumber(this.hauteur.applied_max);

      cards.forEach((card) => {
        card.classList.remove('se-carte--vide');
        const bucket = ETAGE_BUCKETS[card.dataset.etageCarte];
        const bucketMin = bucket.min > 0 ? bucket.min : null;
        const bucketMax = bucket.max !== Infinity ? bucket.max : null;
        const hasApplied = appliedMin !== null || appliedMax !== null;
        const isActive = hasApplied && appliedMin === bucketMin && appliedMax === bucketMax;
        card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      return;
    }

    const activeValues = (this.hauteur.values || [])
      .filter((value) => value.active)
      .map((value) => value.value);

    cards.forEach((card) => {
      const key = card.dataset.etageCarte;
      const values = this.bucketValues(key).map((value) => value.value);
      card.classList.toggle('se-carte--vide', values.length === 0);
      const isActive =
        values.length > 0 &&
        activeValues.length === values.length &&
        values.every((value) => activeValues.includes(value));
      card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  onClick(event) {
    const card = event.target.closest('[data-etage-carte]');
    if (card) {
      event.preventDefault();
      const isActive = card.getAttribute('aria-pressed') === 'true';
      this.applyBucket(isActive ? null : card.dataset.etageCarte);
      return;
    }

    const clearLink = event.target.closest('[data-ref="filters-clear"] a, a[data-ref="filters-clear"]');
    if (clearLink) {
      event.preventDefault();
      this.fetchAndSwap(clearLink.href, true);
      return;
    }

    const paginationLink = event.target.closest('[data-ref="search-pagination"] a');
    if (paginationLink && paginationLink.href) {
      event.preventDefault();
      this.fetchAndSwap(paginationLink.href, true, true);
    }
  }

  onChange(event) {
    if (event.target.closest('[data-ref="search-filters-form"]')) {
      this.submitFilters();
    }
  }

  /* Nouvelle requête tapée dans le champ de la page.
     - Les facettes en cours sont conservées (hidden q du formulaire de filtres).
     - Frappe = replaceState (pas de spam d'historique) ; Entrée = pushState. */
  liveSearch(rawValue, isSubmit) {
    const query = normalizeQuery(String(rawValue).trim());
    if (query.length < 2) return;

    const historyMode = isSubmit ? true : 'replace';
    const form = this.querySelector('[data-ref="search-filters-form"]');

    if (form) {
      const qField = form.querySelector('input[name="q"]');
      if (qField) qField.value = query;
      this.submitFilters(historyMode);
      return;
    }

    /* Page zéro résultat : pas de formulaire de facettes */
    const params = new URLSearchParams();
    params.set('q', query);
    params.set('options[prefix]', 'last');
    this.fetchAndSwap(`${window.location.pathname}?${params.toString()}`, historyMode);
  }

  /* Applique une tranche étage (null = retirer le filtre hauteur) */
  applyBucket(bucketKey) {
    const form = this.querySelector('[data-ref="search-filters-form"]');
    const holder = this.querySelector('[data-ref="hauteur-inputs"]');
    if (!form || !holder || !this.hauteur) return;

    holder.innerHTML = '';

    if (bucketKey) {
      const bucket = ETAGE_BUCKETS[bucketKey];
      if (this.hauteur.type === 'price_range') {
        /* Filtre de type plage numérique : bornes gte / lte */
        if (bucket.min > 0) this.addHidden(holder, this.hauteur.param_min, bucket.min);
        if (bucket.max !== Infinity) this.addHidden(holder, this.hauteur.param_max, bucket.max);
      } else {
        this.bucketValues(bucketKey).forEach((value) => {
          this.addHidden(holder, value.param, value.value);
        });
      }
    }

    this.submitFilters();
  }

  addHidden(holder, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    holder.appendChild(input);
  }

  submitFilters(historyMode) {
    const form = this.querySelector('[data-ref="search-filters-form"]');
    if (!form) return;

    const searchParams = new URLSearchParams();
    for (const [key, value] of new FormData(form).entries()) {
      if (value) searchParams.append(key, value);
    }

    const url = `${window.location.pathname}?${searchParams.toString()}`;
    this.fetchAndSwap(url, historyMode === undefined ? true : historyMode);
  }

  async fetchAndSwap(url, pushState, scrollToGrid) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.classList.add('ms-chargement');

    const separator = url.includes('?') ? '&' : '?';

    try {
      const response = await fetch(`${url}${separator}section_id=${this.sectionId}`, {
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-search-page]');
      const current = this.querySelector('[data-search-page]');

      /* L'utilisateur tape peut-être encore dans le champ q : mémoriser la
         saisie et le curseur pour les restaurer après remplacement du DOM. */
      const active = document.activeElement;
      const typing =
        active && this.contains(active) && active.matches('form[role="search"] input[name="q"]')
          ? { value: active.value, caret: active.selectionStart }
          : null;

      if (fresh && current) {
        current.innerHTML = fresh.innerHTML;
        this.readHauteurData();
        this.syncEtageCards();

        if (typing) {
          const input = this.querySelector('form[role="search"] input[name="q"]');
          if (input) {
            input.value = typing.value;
            input.focus();
            try {
              input.setSelectionRange(typing.caret, typing.caret);
            } catch (error) {
              /* setSelectionRange indisponible sur certains types d'input */
            }
          }
        }
      }

      if (pushState === 'replace') window.history.replaceState({}, '', url);
      else if (pushState) window.history.pushState({}, '', url);

      if (scrollToGrid) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        /* En cas d'échec AJAX, navigation classique : jamais d'écran cassé */
        window.location.href = url;
      }
    } finally {
      this.classList.remove('ms-chargement');
    }
  }
}

if (!customElements.get('search-page-component')) {
  customElements.define('search-page-component', SearchPageComponent);
}

/* Repli au scroll (header sticky) : en haut de page la barre est déployée ;
   passé le seuil, elle se replie et la loupe des actions (à droite du logo)
   prend le relais. État porté par <html>.recherche-repliee, stylé en CSS. */
(() => {
  if (!document.querySelector('.header__fausse-barre')) return;
  /* Hystérésis obligatoire : le repli retire ~55 px au header sticky, et
     l'ancrage de défilement du navigateur compense en réduisant scrollY
     d'autant. Avec un seuil unique, scrollY retombe sous le seuil et la
     barre clignote (effet stroboscopique constaté sur Pixel en descente
     lente). L'écart entre les deux seuils doit dépasser la hauteur de la
     barre pour rendre la boucle impossible. */
  const SEUIL_REPLI = 170;
  const SEUIL_DEPLOI = 30;
  let repliee = window.scrollY > SEUIL_REPLI;
  document.documentElement.classList.toggle('recherche-repliee', repliee);
  window.addEventListener(
    'scroll',
    () => {
      const y = window.scrollY;
      if (!repliee && y > SEUIL_REPLI) {
        repliee = true;
      } else if (repliee && y < SEUIL_DEPLOI) {
        repliee = false;
      } else {
        return;
      }
      document.documentElement.classList.toggle('recherche-repliee', repliee);
    },
    { passive: true }
  );
})();

/* Fausse barre de recherche du header mobile : ouvre le popup de recherche.
   DialogComponent ne câble que son premier [data-ref="popup-toggle"], on
   passe donc par sa méthode publique open(). */
document.addEventListener('click', (event) => {
  const bar = event.target.closest('[data-ref="header-search-bar"]');
  if (!bar) return;
  const scope = bar.closest('.header__layout-mobile') || document;
  const dialog = scope.querySelector('dialog-component.search-popup') || document.querySelector('dialog-component.search-popup');
  if (!dialog || typeof dialog.open !== 'function') return;
  dialog.open();
  const input = dialog.querySelector('input[name="q"]');
  if (input) {
    setTimeout(() => input.focus(), 60);
  }
});

/* Accordéons de facettes (mobile) */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-ref="facette-toggle"]');
  if (!toggle) return;
  if (window.matchMedia('(max-width: 749px)').matches) {
    toggle.closest('.sf-groupe').classList.toggle('sf-groupe--ouvert');
  }
});
